import { createLogger } from "@/logger";

const log = createLogger("telemetry");

import { flags } from "@/config";

export type QueryType = "reactive-select" | "reactive-relational" | "static";

export interface QueryMetric {
  queryName: string;
  queryType: QueryType;
  executionCount: number;
  totalDuration: number;
  avgDuration: number;
  minDuration: number;
  maxDuration: number;
  p50Duration: number;
  lastExecuted: number;
  querySummary: string;
  triggeringTables: Record<string, number>;
}

export interface QueryExecution {
  queryName: string;
  queryType: QueryType;
  duration: number;
  querySummary: string;
  timestamp: number;
  triggeringTable?: string;
  target?: string;
}

class QueryMetricsServiceImpl {
  private metrics = new Map<string, QueryMetric>();
  private executions: QueryExecution[] = [];
  private maxExecutions = 1000;
  private updateListeners = new Set<() => void>();

  private isDebugMode(): boolean {
    return flags.debugMode === true;
  }

  recordQueryExecution(execution: Omit<QueryExecution, "timestamp">): void {
    if (!this.isDebugMode()) {
      return;
    }

    const timestamp = Date.now();
    const { queryName, queryType, duration, querySummary, triggeringTable, target } = execution;

    const key = `${queryType}:${queryName}`;
    const existing = this.metrics.get(key);

    if (existing) {
      const newExecutionCount = existing.executionCount + 1;
      const newTotalDuration = existing.totalDuration + duration;
      const newAvgDuration = newTotalDuration / newExecutionCount;

      const updatedTriggeringTables = { ...existing.triggeringTables };
      if (triggeringTable) {
        updatedTriggeringTables[triggeringTable] = (updatedTriggeringTables[triggeringTable] || 0) + 1;
      }

      this.metrics.set(key, {
        queryName,
        queryType,
        executionCount: newExecutionCount,
        totalDuration: newTotalDuration,
        avgDuration: newAvgDuration,
        minDuration: Math.min(existing.minDuration, duration),
        maxDuration: Math.max(existing.maxDuration, duration),
        p50Duration: 0,
        lastExecuted: timestamp,
        querySummary,
        triggeringTables: updatedTriggeringTables,
      });
    } else {
      const triggeringTables: Record<string, number> = {};
      if (triggeringTable) {
        triggeringTables[triggeringTable] = 1;
      }

      this.metrics.set(key, {
        queryName,
        queryType,
        executionCount: 1,
        totalDuration: duration,
        avgDuration: duration,
        minDuration: duration,
        maxDuration: duration,
        p50Duration: 0,
        lastExecuted: timestamp,
        querySummary,
        triggeringTables,
      });
    }

    const fullExecution: QueryExecution = {
      queryName,
      queryType,
      duration,
      querySummary,
      timestamp,
      triggeringTable,
      target,
    };
    this.executions.push(fullExecution);

    if (this.executions.length > this.maxExecutions) {
      this.executions = this.executions.slice(-this.maxExecutions);
    }

    log.debug(`[QueryMetrics] Recorded ${queryType} query: ${queryName}`, {
      duration,
      executionCount: this.metrics.get(key).executionCount,
      triggeringTable,
      target,
    });

    this.notifyListeners();
  }

  getMetrics(filters?: { queryType?: QueryType; queryName?: string }): QueryMetric[] {
    let results = Array.from(this.metrics.values()).map((metric) => {
      const key = `${metric.queryType}:${metric.queryName}`;
      const queryExecutions = this.executions.filter(
        (e) => e.queryType === metric.queryType && e.queryName === metric.queryName,
      );
      const p50Duration = this.calculateP50(queryExecutions.map((e) => e.duration));

      return {
        ...metric,
        p50Duration,
      };
    });

    if (filters?.queryType) {
      results = results.filter((m) => m.queryType === filters.queryType);
    }

    if (filters?.queryName) {
      results = results.filter((m) => m.queryName.includes(filters.queryName));
    }

    return results.sort((a, b) => b.lastExecuted - a.lastExecuted);
  }

  private calculateP50(durations: number[]): number {
    if (durations.length === 0) return 0;

    const sorted = [...durations].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return (sorted[mid - 1] + sorted[mid]) / 2;
    }
    return sorted[mid];
  }

  getMetricByKey(queryType: QueryType, queryName: string): QueryMetric | undefined {
    const key = `${queryType}:${queryName}`;
    return this.metrics.get(key);
  }

  getExecutions(filters?: {
    queryType?: QueryType;
    queryName?: string;
    startTime?: number;
    endTime?: number;
  }): QueryExecution[] {
    let results = [...this.executions];

    if (filters?.queryType) {
      results = results.filter((e) => e.queryType === filters.queryType);
    }

    if (filters?.queryName) {
      results = results.filter((e) => e.queryName.includes(filters.queryName));
    }

    if (filters?.startTime) {
      results = results.filter((e) => e.timestamp >= filters.startTime);
    }

    if (filters?.endTime) {
      results = results.filter((e) => e.timestamp <= filters.endTime);
    }

    return results.sort((a, b) => b.timestamp - a.timestamp);
  }

  clearMetrics(): void {
    this.metrics.clear();
    this.executions = [];
    log.info("[QueryMetrics] Cleared all query metrics");
    this.notifyListeners();
  }

  getStatistics(queryType?: QueryType): {
    totalQueries: number;
    totalExecutions: number;
    avgExecutionsPerQuery: number;
    totalDuration: number;
    avgDuration: number;
  } {
    const metrics = queryType ? this.getMetrics({ queryType }) : this.getMetrics();

    if (metrics.length === 0) {
      return {
        totalQueries: 0,
        totalExecutions: 0,
        avgExecutionsPerQuery: 0,
        totalDuration: 0,
        avgDuration: 0,
      };
    }

    const totalExecutions = metrics.reduce((sum, m) => sum + m.executionCount, 0);
    const totalDuration = metrics.reduce((sum, m) => sum + m.totalDuration, 0);

    return {
      totalQueries: metrics.length,
      totalExecutions,
      avgExecutionsPerQuery: totalExecutions / metrics.length,
      totalDuration,
      avgDuration: totalDuration / totalExecutions,
    };
  }

  subscribe(callback: () => void): () => void {
    this.updateListeners.add(callback);
    return () => {
      this.updateListeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    this.updateListeners.forEach((listener) => listener());
  }
}

export const QueryMetricsService = new QueryMetricsServiceImpl();
